import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtCurrency, fmtDate } from "@/utils/format";
import {
  Plus, Pencil, Trash2, Check, RotateCcw, Archive, ScrollText, Share2, FileDown, Sheet,
  CalendarDays, List as ListIcon, ChevronLeft, ChevronRight,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import CustomerCombobox from "@/components/CustomerCombobox";

const CHEQUE_STATUS_TR = {
  portfolio: "Portföyde",
  collected: "Tahsil Edildi",
  returned: "İade Edildi",
  endorsed: "Ciro Edildi",
};
const CHEQUE_STATUS_COLORS = {
  portfolio: "bg-yellow-100 text-yellow-800 border-yellow-800",
  collected: "bg-[#00A650] text-white border-[#00A650]",
  returned: "bg-[#FF3B30] text-white border-[#FF3B30]",
  endorsed: "bg-[#0055FF] text-white border-[#0055FF]",
};
const KIND_TR = { cheque: "Çek", note: "Senet" };

async function downloadPdf(path, filename) {
  return downloadBlob(path, filename);
}

async function downloadBlob(path, filename) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const blob = new Blob([res.data], { type: res.headers?.["content-type"] || "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  } catch (e) {
    let msg = "Dosya indirilemedi";
    if (e?.response?.data instanceof Blob) {
      try {
        const text = await e.response.data.text();
        const parsed = JSON.parse(text);
        msg = parsed.detail || parsed.message || text || msg;
      } catch { /* keep default */ }
    } else if (e?.response?.data?.detail) {
      msg = e.response.data.detail;
    }
    // eslint-disable-next-line no-console
    console.error("Download error:", e, msg);
    toast.error(msg);
  }
}

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function Stat({ label, value, sub, tone, testid }) {
  const toneCls =
    tone === "primary" ? "border-l-[#0055FF]" :
    tone === "success" ? "border-l-[#00A650]" :
    tone === "warn" ? "border-l-[#FF9500]" :
    "border-l-[#09090B]";
  return (
    <div className={`bg-white border-2 border-[#09090B] border-l-[6px] ${toneCls} p-5`} data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500">{label}</div>
      <div className="mt-1 text-3xl font-mono tracking-tight font-bold">{value}</div>
      {sub && <div className="mt-1 text-xs font-mono text-neutral-500">{sub}</div>}
    </div>
  );
}

const nowHM = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const emptyChequeRow = () => ({
  customer_id: "", customer_name: "", cheque_no: "", issuer_bank: "",
  amount: 0, due_date: todayISO(), time: nowHM(), kind: "cheque", notes: "",
});

// ---------------- Cheque Calendar (12-month view) ----------------
const TR_MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const TR_WEEKDAYS = ["Pzt", "Sal", "Çrş", "Prş", "Cum", "Cmt", "Paz"];

// Compact TL formatter for calendar cells (e.g. 12500 → "12.5B", 1500000 → "1.5M")
const fmtCompactTL = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `₺${(v / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (v >= 1_000)     return `₺${(v / 1_000).toFixed(1).replace(/\.0$/, "")}B`;
  return `₺${Math.round(v)}`;
};

function ChequeCalendar({ rows }) {
  const [anchor, setAnchor] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };  // month is 0-based
  });
  const [selectedDate, setSelectedDate] = useState(null);

  // Takvim yalnızca portföyde bekleyen (tahsil edilmemiş) çekleri gösterir.
  const portfolioRows = useMemo(
    () => (rows || []).filter((r) => r.status === "portfolio"),
    [rows],
  );

  // 12 months centered on anchor: from -5 to +6 (so anchor is at position 5 — approximately centered in 4x3)
  const months = useMemo(() => {
    const out = [];
    for (let offset = -5; offset <= 6; offset++) {
      const t = new Date(anchor.year, anchor.month + offset, 1);
      out.push({ year: t.getFullYear(), month: t.getMonth() });
    }
    return out;
  }, [anchor]);

  // Group cheques by YYYY-MM-DD
  const byDate = useMemo(() => {
    const m = new Map();
    for (const r of portfolioRows) {
      const d = (r.due_date || "").slice(0, 10);
      if (!d) continue;
      const cur = m.get(d) || { total: 0, count: 0, items: [] };
      cur.total += Number(r.amount || 0);
      cur.count += 1;
      cur.items.push(r);
      m.set(d, cur);
    }
    return m;
  }, [portfolioRows]);

  const monthlyTotal = (y, mo) => {
    let total = 0, count = 0;
    for (const [key, val] of byDate.entries()) {
      const [ky, km] = key.split("-").map(Number);
      if (ky === y && km === mo + 1) { total += val.total; count += val.count; }
    }
    return { total, count };
  };

  const shift = (delta) => setAnchor(({ year, month }) => {
    const t = new Date(year, month + delta, 1);
    return { year: t.getFullYear(), month: t.getMonth() };
  });
  const goToday = () => {
    const d = new Date();
    setAnchor({ year: d.getFullYear(), month: d.getMonth() });
  };

  const todayISOStr = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-4" data-testid="finance-cheque-calendar">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-none border-2 border-[#09090B]"
            onClick={() => shift(-1)} data-testid="calendar-prev-btn">
            <ChevronLeft size={16} /> Önceki Ay
          </Button>
          <Button variant="outline" size="sm" className="rounded-none border-2 border-[#09090B]"
            onClick={goToday} data-testid="calendar-today-btn">
            Bugün
          </Button>
          <Button variant="outline" size="sm" className="rounded-none border-2 border-[#09090B]"
            onClick={() => shift(1)} data-testid="calendar-next-btn">
            Sonraki Ay <ChevronRight size={16} />
          </Button>
        </div>
        <div className="text-xs font-mono uppercase text-neutral-500">
          Merkez: {TR_MONTHS[anchor.month]} {anchor.year} — 12 aylık pencere
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {months.map(({ year, month }) => {
          const firstDay = new Date(year, month, 1);
          const daysInMonth = new Date(year, month + 1, 0).getDate();
          // JS getDay: 0=Sun..6=Sat; convert to Pzt=0..Paz=6
          const leadingBlanks = (firstDay.getDay() + 6) % 7;
          const cells = [];
          for (let i = 0; i < leadingBlanks; i++) cells.push(null);
          for (let d = 1; d <= daysInMonth; d++) cells.push(d);
          while (cells.length % 7 !== 0) cells.push(null);
          const { total, count } = monthlyTotal(year, month);
          const isAnchorMonth = year === anchor.year && month === anchor.month;

          return (
            <div
              key={`${year}-${month}`}
              className={`bg-white border-2 ${isAnchorMonth ? "border-[#0055FF]" : "border-[#09090B]"} p-3`}
              data-testid={`calendar-month-${year}-${String(month + 1).padStart(2, "0")}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="font-bold text-sm tracking-tight">
                  {TR_MONTHS[month]} <span className="text-neutral-500 font-mono text-xs">{year}</span>
                </div>
                {isAnchorMonth && (
                  <span className="px-1.5 py-0.5 border border-[#0055FF] text-[#0055FF] text-[9px] font-mono uppercase">Merkez</span>
                )}
              </div>

              <div className="grid grid-cols-7 gap-[2px] mb-1">
                {TR_WEEKDAYS.map((w) => (
                  <div key={w} className="text-[9px] font-mono uppercase text-center text-neutral-500">{w}</div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-[2px]">
                {cells.map((day, i) => {
                  if (day === null) return <div key={i} className="h-11 bg-neutral-50" />;
                  const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                  const entry = byDate.get(iso);
                  const isToday = iso === todayISOStr;
                  const hasCheques = entry && entry.count > 0;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={hasCheques ? () => setSelectedDate(iso) : undefined}
                      disabled={!hasCheques}
                      className={`h-11 border p-0.5 flex flex-col justify-between text-[10px] font-mono text-left ${
                        hasCheques ? "bg-[#FFF7E6] border-[#FF9500] hover:bg-[#FFEDD5] cursor-pointer" : "border-neutral-200 cursor-default"
                      } ${isToday ? "ring-2 ring-[#0055FF]" : ""}`}
                      title={hasCheques ? `${entry.count} çek · Toplam ${fmtCurrency(entry.total)} · Detayları görmek için tıkla` : undefined}
                      data-testid={`calendar-day-${iso}`}
                    >
                      <div className={`text-right leading-none ${isToday ? "text-[#0055FF] font-bold" : "text-neutral-700"}`}>{day}</div>
                      {hasCheques && (
                        <div
                          className="text-[9px] leading-none text-center font-bold text-[#B45309] truncate"
                          data-testid={`calendar-day-total-${iso}`}
                        >
                          {fmtCompactTL(entry.total)}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="mt-2 pt-2 border-t border-[#09090B] flex items-center justify-between">
                <div className="text-[10px] font-mono uppercase text-neutral-500">Aylık Toplam</div>
                <div
                  className="font-mono font-bold text-sm"
                  data-testid={`calendar-month-total-${year}-${String(month + 1).padStart(2, "0")}`}
                >
                  {fmtCurrency(total)}
                  {count > 0 && <span className="ml-1 text-[9px] text-neutral-500">({count})</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!selectedDate} onOpenChange={(v) => !v && setSelectedDate(null)}>
        <DialogContent className="rounded-none border-2 border-[#09090B] bg-white max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="calendar-day-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">
              {selectedDate && fmtDate(selectedDate)} — Vadeli Çek/Senetler
            </DialogTitle>
          </DialogHeader>
          {selectedDate && (() => {
            const entry = byDate.get(selectedDate);
            if (!entry) return <div className="text-sm text-neutral-500 italic">Bu tarih için portföyde kayıt yok.</div>;
            return (
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-neutral-100 border-2 border-[#09090B] p-3">
                  <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">
                    {entry.count} kayıt
                  </div>
                  <div className="font-mono font-bold text-xl" data-testid="calendar-day-dialog-total">
                    Toplam: {fmtCurrency(entry.total)}
                  </div>
                </div>
                <div className="border-2 border-[#09090B]">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-neutral-100 border-b-2 border-[#09090B]">
                        <TableHead className="font-mono uppercase text-xs">Tür</TableHead>
                        <TableHead className="font-mono uppercase text-xs">Müşteri (Keşideci)</TableHead>
                        <TableHead className="font-mono uppercase text-xs">Çek/Senet No</TableHead>
                        <TableHead className="font-mono uppercase text-xs">Banka</TableHead>
                        <TableHead className="font-mono uppercase text-xs text-center">Saat</TableHead>
                        <TableHead className="font-mono uppercase text-xs text-right">Tutar</TableHead>
                        <TableHead className="font-mono uppercase text-xs">Not</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entry.items.map((c) => (
                        <TableRow key={c.id} data-testid={`calendar-day-dialog-row-${c.id}`}>
                          <TableCell>
                            <span className="inline-block px-2 py-0.5 border-2 border-[#09090B] font-mono uppercase text-[10px] bg-neutral-100">
                              {KIND_TR[c.kind] || "Çek"}
                            </span>
                          </TableCell>
                          <TableCell className="font-semibold">{c.customer_name || "-"}</TableCell>
                          <TableCell className="font-mono text-sm">{c.cheque_no || "-"}</TableCell>
                          <TableCell className="font-mono text-sm">{c.issuer_bank || "-"}</TableCell>
                          <TableCell className="text-center font-mono text-sm">{c.time || "-"}</TableCell>
                          <TableCell className="text-right font-mono font-bold">{fmtCurrency(c.amount)}</TableCell>
                          <TableCell className="text-xs text-neutral-500 max-w-[220px] truncate" title={c.notes || ""}>{c.notes || "-"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={() => setSelectedDate(null)} data-testid="calendar-day-dialog-close">
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ChequesTab({ customers, reloadSummary }) {
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [formRows, setFormRows] = useState([emptyChequeRow()]);
  const [editingId, setEditingId] = useState(null);
  const [endorseTarget, setEndorseTarget] = useState(null);
  const [endorseName, setEndorseName] = useState("");
  const [endorseDate, setEndorseDate] = useState(todayISO());
  const [q, setQ] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [view, setView] = useState("list");

  const load = async () => {
    try {
      const { data } = await api.get(`/finance/cheques?status=${statusFilter}&_ts=${Date.now()}`);
      setRows(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const totals = useMemo(() => {
    const port = rows.filter((r) => r.status === "portfolio");
    return {
      portTotal: port.reduce((s, r) => s + Number(r.amount || 0), 0),
      portCount: port.length,
      shown: rows.reduce((s, r) => s + Number(r.amount || 0), 0),
    };
  }, [rows]);

  const filteredRows = useMemo(() => {
    const ql = q.trim().toLocaleLowerCase("tr-TR");
    return rows.filter((r) => {
      if (startDate && (r.due_date || "") < startDate) return false;
      if (endDate && (r.due_date || "") > endDate) return false;
      if (ql) {
        const hay = `${r.customer_name || ""} ${r.cheque_no || ""} ${r.issuer_bank || ""} ${r.notes || ""} ${r.endorsed_to || ""}`
          .toLocaleLowerCase("tr-TR");
        if (!hay.includes(ql)) return false;
      }
      return true;
    });
  }, [rows, q, startDate, endDate]);
  const filterActive = q || startDate || endDate;
  const clearFilters = () => { setQ(""); setStartDate(""); setEndDate(""); };

  const startNew = () => { setFormRows([emptyChequeRow()]); setEditingId(null); setOpen(true); };
  const startEdit = (c) => {
    setFormRows([{
      customer_id: c.customer_id || "",
      customer_name: c.customer_name,
      cheque_no: c.cheque_no || "",
      issuer_bank: c.issuer_bank || "",
      amount: c.amount,
      due_date: (c.due_date || "").slice(0, 10),
      time: c.time || "",
      kind: c.kind || "cheque",
      notes: c.notes || "",
    }]);
    setEditingId(c.id); setOpen(true);
  };
  const addChequeRow = () => setFormRows([...formRows, emptyChequeRow()]);
  const removeChequeRow = (i) => setFormRows(formRows.length === 1 ? [emptyChequeRow()] : formRows.filter((_, idx) => idx !== i));
  const updateChequeRow = (i, patch) => setFormRows(formRows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const save = async () => {
    for (const r of formRows) {
      if (!r.customer_name.trim()) { toast.error("Tüm satırlarda müşteri zorunlu"); return; }
      if (!r.amount || Number(r.amount) <= 0) { toast.error("Tüm satırlarda tutar 0'dan büyük olmalı"); return; }
      if (!r.due_date) { toast.error("Tüm satırlarda vade tarihi zorunlu"); return; }
    }
    try {
      const cleaned = formRows.map((r) => ({ ...r, amount: Number(r.amount), customer_id: r.customer_id || null }));
      if (editingId) {
        await api.put(`/finance/cheques/${editingId}`, cleaned[0]);
        toast.success("Güncellendi");
      } else {
        await api.post("/finance/cheques/bulk", cleaned);
        toast.success(`${cleaned.length} kayıt eklendi`);
      }
      setOpen(false); load(); reloadSummary();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const setStatus = async (c, newStatus) => {
    try {
      await api.post(`/finance/cheques/${c.id}/status`, { status: newStatus });
      toast.success(`Durum: ${CHEQUE_STATUS_TR[newStatus]}`);
      load(); reloadSummary();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openCollect = async (c) => {
    try {
      await api.post(`/finance/cheques/${c.id}/status`, { status: "collected", collected_date: todayISO() });
      toast.success("Çek tahsil edildi");
      load(); reloadSummary();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openEndorse = (c) => { setEndorseTarget(c); setEndorseName(""); setEndorseDate(todayISO()); };
  const confirmEndorse = async () => {
    if (!endorseName.trim()) { toast.error("Ciro edilecek kişi/kurum adı zorunlu"); return; }
    if (!endorseDate) { toast.error("Ciro tarihi zorunlu"); return; }
    try {
      await api.post(`/finance/cheques/${endorseTarget.id}/status`, {
        status: "endorsed",
        endorsed_to: endorseName.trim(),
        endorsed_date: endorseDate,
      });
      toast.success(`Ciro edildi: ${endorseName.trim()} (${fmtDate(endorseDate)})`);
      setEndorseTarget(null); setEndorseName(""); setEndorseDate(todayISO());
      load(); reloadSummary();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (c) => {
    if (!window.confirm("Bu kayıt silinsin mi?")) return;
    try { await api.delete(`/finance/cheques/${c.id}`); toast.success("Silindi"); load(); reloadSummary(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="finance-cheques-tab">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <Label className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Durum Filtresi</Label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="finance-cheques-status-filter"
            className="block mt-1 h-10 rounded-none border-2 border-[#09090B] font-mono text-sm px-2 bg-white focus:outline-none"
          >
            <option value="all">Tümü</option>
            <option value="portfolio">Portföyde (Tahsil Edilmemiş)</option>
            <option value="collected">Tahsil Edildi</option>
            <option value="returned">İade Edildi</option>
            <option value="endorsed">Ciro Edildi</option>
          </select>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Portföy Toplamı</div>
          <div className="text-2xl font-mono font-bold" data-testid="finance-cheques-portfolio-total">{fmtCurrency(totals.portTotal)}</div>
          <div className="text-[10px] font-mono text-neutral-500">{totals.portCount} kayıt</div>
        </div>
        <Button className="rounded-none bg-[#09090B] hover:bg-[#0055FF]" onClick={startNew} data-testid="finance-new-cheque-btn">
          <Plus size={16} className="mr-2" /> Yeni Çek / Senet
        </Button>
      </div>

      <div className="flex items-center gap-2 border-2 border-[#09090B] bg-white p-1 w-fit" data-testid="finance-view-toggle">
        <button
          onClick={() => setView("list")}
          className={`px-3 py-1.5 font-mono uppercase text-xs tracking-widest flex items-center gap-2 ${
            view === "list" ? "bg-[#09090B] text-white" : "text-[#09090B]"
          }`}
          data-testid="finance-view-list-btn"
        >
          <ListIcon size={14} /> Liste
        </button>
        <button
          onClick={() => setView("calendar")}
          className={`px-3 py-1.5 font-mono uppercase text-xs tracking-widest flex items-center gap-2 ${
            view === "calendar" ? "bg-[#09090B] text-white" : "text-[#09090B]"
          }`}
          data-testid="finance-view-calendar-btn"
        >
          <CalendarDays size={14} /> Takvim
        </button>
      </div>

      {view === "calendar" ? (
        <ChequeCalendar rows={filteredRows} />
      ) : (
      <>
      <div className="bg-white border-2 border-[#09090B] p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <div className="md:col-span-2">
          <Label className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Kelime Ara</Label>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Müşteri, çek no, banka, not, ciro adı…"
            className="rounded-none border-2 border-[#09090B] focus-visible:ring-0"
            data-testid="finance-cheques-search"
          />
        </div>
        <div>
          <Label className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Vade Başlangıç</Label>
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
            className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="finance-cheques-start-date" />
        </div>
        <div>
          <Label className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Vade Bitiş</Label>
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
            className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="finance-cheques-end-date" />
        </div>
        <div className="md:col-span-4 flex items-center justify-between">
          <div className="text-xs font-mono text-neutral-500" data-testid="finance-cheques-filter-count">
            {filteredRows.length} / {rows.length} kayıt
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm"
              className="rounded-none border-2 border-[#09090B]"
              onClick={() => downloadPdf(
                `/finance/cheques/pdf?q=${encodeURIComponent(q)}&start=${startDate}&end=${endDate}&status=${statusFilter}`,
                "cek_senet_listesi.pdf"
              )}
              data-testid="finance-cheques-export-pdf"
            >
              <FileDown size={14} className="mr-1" /> PDF
            </Button>
            <Button
              variant="outline" size="sm"
              className="rounded-none border-2 border-[#09090B]"
              onClick={() => downloadBlob(
                `/finance/cheques/xlsx?q=${encodeURIComponent(q)}&start=${startDate}&end=${endDate}&status=${statusFilter}`,
                "cek_senet_listesi.xlsx"
              )}
              data-testid="finance-cheques-export-xlsx"
            >
              <Sheet size={14} className="mr-1" /> Excel
            </Button>
            {filterActive && (
              <Button variant="outline" size="sm" className="rounded-none border-2 border-[#09090B]" onClick={clearFilters} data-testid="finance-cheques-clear-filters">
                Filtreleri Temizle
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border-2 border-[#09090B]">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-100 border-b-2 border-[#09090B]">
              <TableHead className="font-mono uppercase text-xs">Tür</TableHead>
              <TableHead className="font-mono uppercase text-xs">Müşteri</TableHead>
              <TableHead className="font-mono uppercase text-xs">No</TableHead>
              <TableHead className="font-mono uppercase text-xs">Vade</TableHead>
              <TableHead className="font-mono uppercase text-xs text-right">Tutar</TableHead>
              <TableHead className="font-mono uppercase text-xs">Durum</TableHead>
              <TableHead className="font-mono uppercase text-xs text-right">İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRows.map((c) => (
              <TableRow key={c.id} data-testid={`finance-cheque-row-${c.id}`}>
                <TableCell>
                  <span className="inline-block px-2 py-0.5 border-2 border-[#09090B] font-mono uppercase text-[10px] bg-neutral-100">
                    {KIND_TR[c.kind] || "Çek"}
                  </span>
                </TableCell>
                <TableCell className="font-semibold">{c.customer_name}</TableCell>
                <TableCell className="font-mono text-sm text-neutral-500">{c.cheque_no || "-"}</TableCell>
                <TableCell className="font-mono text-sm">{fmtDate(c.due_date)}{c.time ? ` · ${c.time}` : ""}</TableCell>
                <TableCell className="text-right font-mono font-bold">{fmtCurrency(c.amount)}</TableCell>
                <TableCell>
                  <span className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase border ${CHEQUE_STATUS_COLORS[c.status]}`}>
                    {CHEQUE_STATUS_TR[c.status]}
                  </span>
                  {c.status === "endorsed" && c.endorsed_to && (
                    <div className="mt-1 text-[10px] font-mono text-neutral-500" data-testid={`finance-cheque-endorsed-to-${c.id}`}>
                      → {c.endorsed_to}{c.endorsed_date ? ` · ${fmtDate(c.endorsed_date)}` : ""}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {c.status === "portfolio" && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => openCollect(c)} title="Tahsil Et" data-testid={`finance-cheque-collect-${c.id}`}>
                        <Check size={14} className="text-[#00A650]" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEndorse(c)} title="Ciro Et" data-testid={`finance-cheque-endorse-${c.id}`}>
                        <Share2 size={14} className="text-[#0055FF]" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setStatus(c, "returned")} title="İade Et" data-testid={`finance-cheque-return-${c.id}`}>
                        <RotateCcw size={14} className="text-[#FF3B30]" />
                      </Button>
                    </>
                  )}
                  {c.status !== "portfolio" && (
                    <Button size="sm" variant="ghost" onClick={() => setStatus(c, "portfolio")} title="Portföye Al" data-testid={`finance-cheque-portfolio-${c.id}`}>
                      <Archive size={14} />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => startEdit(c)} data-testid={`finance-edit-cheque-${c.id}`}><Pencil size={14} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(c)} data-testid={`finance-delete-cheque-${c.id}`}><Trash2 size={14} className="text-red-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {filteredRows.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-neutral-500">
                <ScrollText size={20} className="mx-auto mb-2 opacity-40" />
                {rows.length === 0 ? "Kayıt yok" : "Filtreye uyan kayıt yok"}
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-2 border-[#09090B] bg-white max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl tracking-tight">
            {editingId ? "Çek / Senet Düzenle" : "Yeni Çek / Senet"}
          </DialogTitle></DialogHeader>
          <div className="grid gap-3">
            {formRows.map((f, i) => (
              <div key={i} className="border-2 border-[#09090B] p-3 grid gap-2" data-testid={`finance-cheque-row-form-${i}`}>
                {!editingId && (
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Satır #{i + 1}</div>
                    {formRows.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeChequeRow(i)} data-testid={`finance-cheque-row-remove-${i}`}>
                        <Trash2 size={14} className="text-red-600" />
                      </Button>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => updateChequeRow(i, { kind: "cheque" })}
                    data-testid={`finance-cheque-kind-cheque-${i}`}
                    className={`h-10 border-2 border-[#09090B] font-mono uppercase text-xs ${f.kind === "cheque" ? "bg-[#0055FF] text-white" : "bg-white"}`}>Çek</button>
                  <button type="button" onClick={() => updateChequeRow(i, { kind: "note" })}
                    data-testid={`finance-cheque-kind-note-${i}`}
                    className={`h-10 border-2 border-[#09090B] font-mono uppercase text-xs ${f.kind === "note" ? "bg-[#0055FF] text-white" : "bg-white"}`}>Senet</button>
                </div>
                <div>
                  <Label className="text-[10px] font-mono uppercase">Müşteri</Label>
                  <CustomerCombobox
                    customers={customers}
                    value={f.customer_id}
                    onChange={(c) => updateChequeRow(i, { customer_id: c ? c.id : "", customer_name: c ? c.name : "" })}
                    testid={`finance-cheque-customer-combobox-${i}`}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] font-mono uppercase">Çek/Senet No</Label>
                    <Input value={f.cheque_no} onChange={(e) => updateChequeRow(i, { cheque_no: e.target.value })}
                      className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid={`finance-cheque-no-input-${i}`} />
                  </div>
                  <div>
                    <Label className="text-[10px] font-mono uppercase">Bankası</Label>
                    <Input value={f.issuer_bank} onChange={(e) => updateChequeRow(i, { issuer_bank: e.target.value })}
                      className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid={`finance-cheque-issuer-input-${i}`} />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-[10px] font-mono uppercase">Tutar</Label>
                    <Input type="number" step="0.01" value={f.amount} onChange={(e) => updateChequeRow(i, { amount: e.target.value })}
                      className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid={`finance-cheque-amount-input-${i}`} />
                  </div>
                  <div>
                    <Label className="text-[10px] font-mono uppercase">Vade Tarihi</Label>
                    <Input type="date" value={f.due_date} onChange={(e) => updateChequeRow(i, { due_date: e.target.value })}
                      className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid={`finance-cheque-due-input-${i}`} />
                  </div>
                  <div>
                    <Label className="text-[10px] font-mono uppercase">Saat</Label>
                    <Input type="time" value={f.time} onChange={(e) => updateChequeRow(i, { time: e.target.value })}
                      className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid={`finance-cheque-time-input-${i}`} />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] font-mono uppercase">Notlar</Label>
                  <Input value={f.notes} onChange={(e) => updateChequeRow(i, { notes: e.target.value })}
                    className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid={`finance-cheque-notes-input-${i}`} />
                </div>
              </div>
            ))}
            {!editingId && (
              <Button variant="outline" onClick={addChequeRow} className="rounded-none border-2 border-dashed border-[#09090B] w-full" data-testid="finance-cheque-add-row">
                <Plus size={14} className="mr-2" /> Satır Ekle
              </Button>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={() => setOpen(false)}>İptal</Button>
            <Button className="rounded-none bg-[#09090B] hover:bg-[#0055FF]" onClick={save} data-testid="finance-save-cheque-btn">
              Kaydet{editingId ? "" : ` (${formRows.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!endorseTarget} onOpenChange={(v) => !v && setEndorseTarget(null)}>
        <DialogContent className="rounded-none border-2 border-[#09090B] bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-2xl tracking-tight">Çeki / Senedi Ciro Et</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div className="bg-neutral-100 border-2 border-[#09090B] p-3 text-sm font-mono">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Kayıt</div>
              <div className="font-semibold">
                {endorseTarget?.customer_name} · {fmtCurrency(endorseTarget?.amount || 0)} · Vade {fmtDate(endorseTarget?.due_date)}
              </div>
              {endorseTarget?.cheque_no && <div className="text-neutral-500 text-xs">No: {endorseTarget.cheque_no}</div>}
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Ciro Edilen Kişi / Kurum</Label>
              <Input
                autoFocus
                value={endorseName}
                onChange={(e) => setEndorseName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") confirmEndorse(); }}
                placeholder="Örn: ABC Ltd. Şti."
                className="rounded-none border-2 border-[#09090B] focus-visible:ring-0"
                data-testid="finance-endorse-name-input"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Ciro Tarihi</Label>
              <Input
                type="date"
                value={endorseDate}
                onChange={(e) => setEndorseDate(e.target.value)}
                className="rounded-none border-2 border-[#09090B] focus-visible:ring-0"
                data-testid="finance-endorse-date-input"
              />
            </div>
            <div className="text-[10px] font-mono text-neutral-500">
              Ciro edilen çek portföyden çıkar, hareket geçmişine "Ciro edildi — [ad]" olarak kaydedilir.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={() => setEndorseTarget(null)}>İptal</Button>
            <Button className="rounded-none bg-[#0055FF] hover:bg-[#09090B]" onClick={confirmEndorse} data-testid="finance-confirm-endorse-btn">
              <Share2 size={14} className="mr-2" /> Ciro Et
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Main page ----------------
export default function Finance() {
  const [customers, setCustomers] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const loadAll = async () => {
    setRefreshing(true);
    try {
      const c = await api.get(`/customers?_ts=${Date.now()}`);
      setCustomers(c.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setRefreshing(false); }
  };
  useEffect(() => { loadAll(); }, []);

  return (
    <div className="space-y-6" data-testid="finance-page">
      <PageHeader
        kicker="Finans"
        title="Çek / Senet Yönetimi"
        subtitle="Çek / senet portföyünüzü buradan takip edin."
        onRefresh={loadAll}
        refreshing={refreshing}
      />
      <ChequesTab customers={customers} reloadSummary={loadAll} />
    </div>
  );
}
